import { handleApiError, handleApiResponse } from "@/methods/api";
import ApiVerified from "@/services/apiVerified";
import {
  AllActivityStudentMarks,
  BatchActivitySummary,
  StudentActivitySummary,
} from "@/types/activity";
import { StudentMarksheetReportDisplay, StudentMarksheetReportInputs } from "@/types/admin";
import { BatchEffortAnalysisData, SyllabusBatchWiseData } from "@/types/affiliation";
import { ApiResponse, PaginatedResponse } from "@/types/global";
import { LeaderboardCard } from "@/types/studentDashboard";
import { ActivityWiseRecommendations, OverviewCard, PerformanceCards } from "@/types/userMetrics";

export default class AffiliationReportApi extends ApiVerified {
  async getBatchWiseSyllabusCoverage(
    affiliationId: number,
    affiliationProgramId: number,
    startDate: string,
    endDate: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<SyllabusBatchWiseData>>(
        `affiliation-report/batch-wise-syllabus-coverage`,
        {
          params: {
            affiliationId: affiliationId,
            affiliationProgramId: affiliationProgramId,
            startDate: startDate,
            endDate: endDate,
          },
        },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getBatchEffortAnalysis(
    batchId: number,
    startDate: string,
    endDate: string,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<BatchEffortAnalysisData>(
        `affiliation-report/batch-wise-effort-analysis`,
        { params: { batchId: batchId, startDate: startDate, endDate: endDate } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getBatchPerformanceOverviewCards(
    batchId: number,
    subjectId: number,
    isDemoUser: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<OverviewCard>>(
        `affiliation-report/get-batch-performance-overview-cards`,
        { params: { batchId: batchId, subjectId: subjectId, isDemoUser: isDemoUser } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getBatchPerformanceStudentWiseCards(
    batchId: number,
    subjectId: number,
    isDemoUser: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PerformanceCards>(
        `affiliation-report/get-batch-performance-studentwise-cards`,
        { params: { batchId: batchId, subjectId: subjectId, isDemoUser: isDemoUser } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getBatchPerformanceChapterWiseCards(
    batchId: number,
    subjectId: number,
    isDemoUser: boolean,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<PerformanceCards>(
        `affiliation-report/get-batch-performance-chapterwise-cards`,
        { params: { batchId: batchId, subjectId: subjectId, isDemoUser: isDemoUser } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getTeacherRecommendationCards(
    batchId: number,
    isDemoUser: boolean,
    subjectId: number | null,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<ActivityWiseRecommendations>(
        `affiliation-report/get-teacher-recommendation-cards`,
        { params: { batchId: batchId, subjectId: subjectId, isDemoUser: isDemoUser } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getBatchLeaderboard(
    batchId: number,
    isDemoUser: boolean,
    subjectId: number | null,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<LeaderboardCard>>(
        `affiliation-report/get-batch-leaderboard`,
        { params: { batchId: batchId, isDemoUser: isDemoUser, subjectId: subjectId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async submitStudentMarksheetData(
    reportInputs: StudentMarksheetReportInputs,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.post<boolean>(
        `affiliation-report/submit-student-marksheet-data`,
        reportInputs,
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async updateStudentMarksheetReportCsv(mongoId: string, csvFile: string): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.put<boolean>(
        `affiliation-report/update-student-marksheet-report-csv`,
        { MongoId: mongoId, CsvFile: csvFile },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getStudentMarksheetReports(
    pageNumber: number,
    pageSize: number,
    batchId: number | null,
    userId: number | null,
  ): Promise<ApiResponse> {
    try {
      // Paginated wire envelope { items, count, pages } — unwrapped by handleApiResponse(_, true) (AR-21.4).
      const response = await this.axiosInstance.get<PaginatedResponse<StudentMarksheetReportDisplay>>(
        `affiliation-report/get-all-student-marksheet-reports`,
        {
          params: { batchId: batchId, userId: userId, pageNumber: pageNumber, pageSize: pageSize },
        },
      );
      return handleApiResponse(response, true);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAllTestsSummaryForBatch(batchId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<BatchActivitySummary>>(
        `affiliation-report/get-all-tests-summary-for-batch`,
        { params: { batchId: batchId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  // Keyed by the CourseSubject junction (backend PR #872) — a raw SubjectId returns nothing.
  async getInstituteTestPerformaceForStudent(
    userId: number,
    batchId: number,
    courseSubjectId: number,
  ): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<Array<StudentActivitySummary>>(
        `affiliation-report/get-institute-test-performance-for-student`,
        { params: { userId: userId, batchId: batchId, courseSubjectId: courseSubjectId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }

  async getAllTestsStudentMarksForBatch(batchId: number): Promise<ApiResponse> {
    try {
      const response = await this.axiosInstance.get<AllActivityStudentMarks>(
        `affiliation-report/get-all-test-student-marks-for-batch`,
        { params: { batchId: batchId } },
      );
      return handleApiResponse(response);
    } catch (error) {
      return handleApiError(error);
    }
  }
}
